import { action, computed, makeObservable, observable, toJS } from "mobx";
import { DbExtensionSettings } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";
import { ExtensionSettings, ExtensionSettingsPartial, ExtensionSettingsUpdate, defaultSettings } from "../../lib/MultiWorld/ExtensionSettings";
import { lazyObservable } from "mobx-utils";
import { getUserChosenDirectoryHandle, verifyFileSystemPermission } from "../FileSystem";
import { ExtensionSettingsUpdated } from "../StateEvents";

export class DbExtensionSettingsTable {

	static readLatest = async () => {
		const db = await requireEvidenceDatabase();
		const tx = db.transaction("extensionSettings", "readonly");
		try {
			// Get reverse cursor to pull out the last element
			const cursor = await tx.store.openCursor(null, 'prev');
			tx.commit();
			return cursor?.value ?? { settings: {} };
		} catch (e) {
			tx.abort();
			throw e;
		}
	}

	static write = async (dbExtensionSettings: DbExtensionSettings, writeNew: boolean = false) => {
		const db = await requireEvidenceDatabase();
		const {settingsIndex: settingsIndexOrUndefined, ...dbExtensionSettingsWithoutSettingsIndex} = dbExtensionSettings;
		let settingsIndex = settingsIndexOrUndefined;
		if (typeof settingsIndex === "number" && !writeNew) {
			 settingsIndex = await db.put("extensionSettings", dbExtensionSettings);
		} else {
			 settingsIndex = await db.add("extensionSettings", dbExtensionSettingsWithoutSettingsIndex);
		}
		
		ExtensionSettingsUpdated.send({});
		return settingsIndex;
	}
}

export class ExtensionSettingsAccessor {
	settingsIndex?: number;
	protected _settingsSpecified: ExtensionSettingsPartial;
	protected updateSettingsSpecified = action ((settings: ExtensionSettingsPartial) => {
		this._settingsSpecified = settings;
	});

	@computed get settingsSpecified() {
		return {...this._settingsSpecified};
	}
	@computed get settings(): ExtensionSettings {
		return {...defaultSettings, ...this._settingsSpecified};
	}

	@computed get sharedSearchTermField() {
		return this.settings.sharedSearchTerms ?? "";
	}

	@computed get sharedSearchTermArray() {
		return this.sharedSearchTermField.split("\n").map( s => s.trim() ).filter( s => s.length > 0);
	}

	protected constructor(initialSettings: DbExtensionSettings) {
		this.settingsIndex = initialSettings.settingsIndex;
		this._settingsSpecified = initialSettings.settings;
		makeObservable<this, "_settingsSpecified">(this, {
			"_settingsSpecified": observable
		});
		ExtensionSettingsUpdated.addListener( async () => {
			const {settingsIndex, settings} = await DbExtensionSettingsTable.readLatest();;
			this.settingsIndex = settingsIndex;
			this.updateSettingsSpecified(settings);
		});
	}

	static #create = async () => {
		return new ExtensionSettingsAccessor(await DbExtensionSettingsTable.readLatest());
	}
	static #instancePromise: Promise<ExtensionSettingsAccessor> | undefined;
	@computed static get instance() {
		return ExtensionSettingsAccessor.#instancePromise ?? (ExtensionSettingsAccessor.#instancePromise = ExtensionSettingsAccessor.#create());
	}

	@computed static get lazy() {
		return lazyObservable<ExtensionSettingsAccessor>( async (sink) =>
			sink(await ExtensionSettingsAccessor.instance))
	}
	@computed static get current() {
		return ExtensionSettingsAccessor.lazy.current()
	}

	getArchiveDirectoryWithPermissions = async () => {
		const directory = this.settings.archiveDirectoryHandle;
		if (directory != null && await verifyFileSystemPermission(directory)) {
			return directory;
		}
		return undefined;
	}

	updateSettingObservable = action (<KEY extends keyof ExtensionSettingsUpdate>(key: KEY, value: ExtensionSettingsUpdate[KEY]) => {
    if (value == null) {
      delete this._settingsSpecified[key];
    } else {
      this._settingsSpecified[key] = value;
    }
  })

	#write = async () => {
		const {settingsIndex, settingsSpecified} = this;
		this.settingsIndex = await DbExtensionSettingsTable.write({settingsIndex, settings: toJS(settingsSpecified)});
  }

	updateSetting = async <KEY extends keyof ExtensionSettingsUpdate>(key: KEY, value: ExtensionSettingsUpdate[KEY]) => {
    this.updateSettingObservable(key, value);
    await this.#write();
  }

	static updateSetting = async <KEY extends keyof ExtensionSettingsUpdate>(key: KEY, value: ExtensionSettingsUpdate[KEY]) => {
		const instance = await ExtensionSettingsAccessor.instance;
		instance.updateSettingObservable(key, value);
		await instance.#write();
	}

  updateSettings = async (newSettings?: ExtensionSettingsUpdate) => {
    if (newSettings == null) return;
    for (const [key, value] of Object.entries(newSettings) as [keyof ExtensionSettingsUpdate, ExtensionSettingsUpdate[keyof ExtensionSettingsUpdate]][]) {
      this.updateSettingObservable(key, value);
    }
    await this.#write();
  };

}

export const extensionSettingsIfLoaded = () => {
	lazyObservable<DbExtensionSettingsTable>( async (sink) => sink(await ExtensionSettingsAccessor.instance) ).current();
};

export const getDirectoryToSaveInto = async () => {
	const settingsController = await ExtensionSettingsAccessor.instance;
	const directoryHandle = await getUserChosenDirectoryHandle({
		startIn: settingsController.settings.archiveDirectoryHandle,
		mode: 'readwrite'
	});
	return directoryHandle;
}